import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { getWorker, createWorker, updateWorker, getClients } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ArrowLeft, Save, User, FileText, FolderOpen, Layers } from 'lucide-react';
import { toast } from 'sonner';
import { formatDate, getExpiryStatusClass, getExpiryStatusLabel } from '@/lib/utils';
import DocumentsTab from '@/components/DocumentsTab';
import CustomFieldsRenderer from '@/components/CustomFieldsRenderer';

const visaTypes = ['Employment', 'Investor', 'Partner', 'Family', 'Domestic', 'Golden'];
const genders = ['Male', 'Female'];
const workerStatuses = ['Active', 'Inactive', 'Cancelled', 'On Hold', 'Absconded'];

export default function WorkerDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isNew = !id || id === 'new';
  const isEdit = window.location.pathname.endsWith('/edit');

  const [worker, setWorker] = useState({
    client_id: searchParams.get('client_id') || '',
    full_name: '',
    nationality: '',
    gender: '',
    dob: '',
    passport_no: '',
    passport_expiry: '',
    visa_no: '',
    visa_expiry: '',
    visa_type: '',
    emirates_id_no: '',
    emirates_id_expiry: '',
    labor_card_no: '',
    labor_card_expiry: '',
    job_title: '',
    mobile: '',
    email: '',
    notes: '',
    status: 'Active',
    custom_fields: {}
  });
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const loadClients = useCallback(async () => {
    try {
      const res = await getClients({ limit: 1000, client_type: 'Company' });
      setClients(res.data.items);
    } catch (error) {
      console.error('Failed to load clients');
    }
  }, []);

  const loadWorker = useCallback(async () => {
    try {
      const res = await getWorker(id);
      setWorker(res.data);
    } catch (error) {
      toast.error('Failed to load worker');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadClients();
    if (!isNew && id) {
      setLoading(true);
      loadWorker();
    }
  }, [id, isNew, loadClients, loadWorker]);

  const handleSave = async () => {
    if (!worker.client_id) {
      toast.error('Please select a company');
      return;
    }
    if (!worker.full_name) {
      toast.error('Please enter worker name');
      return;
    }
    
    setSaving(true);
    try {
      if (isNew) {
        const res = await createWorker(worker);
        toast.success('Worker created');
        navigate(`/workers/${res.data.id}`);
      } else {
        await updateWorker(id, worker);
        toast.success('Worker updated');
        navigate(`/workers/${id}`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save worker');
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field, value) => {
    setWorker((prev) => ({ ...prev, [field]: value }));
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64">Loading...</div>;
  }

  // Form view for new/edit
  if (isNew || isEdit) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate('/workers')}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <h1 className="text-2xl font-bold">{isNew ? 'New Worker' : 'Edit Worker'}</h1>
        </div>

        <div className="grid gap-6">
          {/* Basic Info */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Basic Information</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <div>
                  <Label>Company *</Label>
                  <Select 
                    value={worker.client_id} 
                    onValueChange={(v) => handleChange('client_id', v)}
                  >
                    <SelectTrigger data-testid="worker-client">
                      <SelectValue placeholder="Select company" />
                    </SelectTrigger>
                    <SelectContent>
                      {clients.map((c) => (
                        <SelectItem key={c.id} value={c.id}>{c.display_name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Full Name *</Label>
                  <Input
                    value={worker.full_name || ''}
                    onChange={(e) => handleChange('full_name', e.target.value)}
                    data-testid="worker-name"
                  />
                </div>
                <div>
                  <Label>Job Title</Label>
                  <Input
                    value={worker.job_title || ''}
                    onChange={(e) => handleChange('job_title', e.target.value)}
                  />
                </div>
                <div>
                  <Label>Nationality</Label>
                  <Input
                    value={worker.nationality || ''}
                    onChange={(e) => handleChange('nationality', e.target.value)}
                  />
                </div>
                <div>
                  <Label>Gender</Label>
                  <Select value={worker.gender || ''} onValueChange={(v) => handleChange('gender', v)}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent>
                      {genders.map((g) => (
                        <SelectItem key={g} value={g}>{g}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Date of Birth</Label>
                  <Input
                    type="date"
                    value={worker.dob || ''}
                    onChange={(e) => handleChange('dob', e.target.value)}
                  />
                </div>
                <div>
                  <Label>Mobile</Label>
                  <Input
                    value={worker.mobile || ''}
                    onChange={(e) => handleChange('mobile', e.target.value)}
                  />
                </div>
                <div>
                  <Label>Email</Label>
                  <Input
                    type="email"
                    value={worker.email || ''}
                    onChange={(e) => handleChange('email', e.target.value)}
                  />
                </div>
                {!isNew && (
                  <div>
                    <Label>Status</Label>
                    <Select value={worker.status || 'Active'} onValueChange={(v) => handleChange('status', v)}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {workerStatuses.map((s) => (
                          <SelectItem key={s} value={s}>{s}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Documents */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Documents</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="space-y-2">
                  <Label className="font-semibold">Passport</Label>
                  <Input
                    placeholder="Passport No"
                    value={worker.passport_no || ''}
                    onChange={(e) => handleChange('passport_no', e.target.value)}
                  />
                  <Input
                    type="date"
                    value={worker.passport_expiry || ''}
                    onChange={(e) => handleChange('passport_expiry', e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label className="font-semibold">Visa</Label>
                  <Input
                    placeholder="Visa No"
                    value={worker.visa_no || ''}
                    onChange={(e) => handleChange('visa_no', e.target.value)}
                  />
                  <Input
                    type="date"
                    value={worker.visa_expiry || ''}
                    onChange={(e) => handleChange('visa_expiry', e.target.value)}
                    data-testid="worker-visa-expiry"
                  />
                  <Select value={worker.visa_type || ''} onValueChange={(v) => handleChange('visa_type', v)}>
                    <SelectTrigger>
                      <SelectValue placeholder="Visa Type" />
                    </SelectTrigger>
                    <SelectContent>
                      {visaTypes.map((t) => (
                        <SelectItem key={t} value={t}>{t}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="font-semibold">Emirates ID</Label>
                  <Input
                    placeholder="EID No"
                    value={worker.emirates_id_no || ''}
                    onChange={(e) => handleChange('emirates_id_no', e.target.value)}
                  />
                  <Input
                    type="date"
                    value={worker.emirates_id_expiry || ''}
                    onChange={(e) => handleChange('emirates_id_expiry', e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label className="font-semibold">Labour Card</Label>
                  <Input
                    placeholder="Card No"
                    value={worker.labor_card_no || ''}
                    onChange={(e) => handleChange('labor_card_no', e.target.value)}
                  />
                  <Input
                    type="date"
                    value={worker.labor_card_expiry || ''}
                    onChange={(e) => handleChange('labor_card_expiry', e.target.value)}
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Notes */}
          <Card>
            <CardContent className="pt-4">
              <Label>Notes</Label>
              <Textarea
                value={worker.notes || ''}
                onChange={(e) => handleChange('notes', e.target.value)}
                rows={3}
              />
            </CardContent>
          </Card>

          {/* Custom Fields */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <Layers className="w-4 h-4" />
                Additional Information
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <CustomFieldsRenderer
                  entityType="Worker"
                  values={worker.custom_fields || {}}
                  onChange={(fieldName, value) => {
                    setWorker(prev => ({
                      ...prev,
                      custom_fields: {
                        ...prev.custom_fields,
                        [fieldName]: value
                      }
                    }));
                  }}
                  mode="form"
                />
              </div>
            </CardContent>
          </Card>

          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => navigate(isNew ? '/workers' : `/workers/${id}`)}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving} data-testid="save-worker-btn">
              <Save className="w-4 h-4 mr-2" />
              {saving ? 'Saving...' : 'Save Worker'}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Detail view
  const docCards = [
    { label: 'Passport', no: worker.passport_no, expiry: worker.passport_expiry, days: worker.passport_expiry_days, status: worker.passport_expiry_status },
    { label: 'Visa', no: worker.visa_no, expiry: worker.visa_expiry, days: worker.visa_expiry_days, status: worker.visa_expiry_status, extra: worker.visa_type },
    { label: 'Emirates ID', no: worker.emirates_id_no, expiry: worker.emirates_id_expiry, days: worker.emirates_id_expiry_days, status: worker.emirates_id_expiry_status },
    { label: 'Labour Card', no: worker.labor_card_no, expiry: worker.labor_card_expiry, days: worker.labor_card_expiry_days, status: worker.labor_card_expiry_status }
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate('/workers')}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <div>
            <div className="flex items-center gap-2">
              <User className="w-6 h-6 text-primary" />
              <h1 className="text-2xl font-bold">{worker.full_name}</h1>
              <Badge variant="secondary">{worker.status}</Badge>
            </div>
            <p className="text-sm text-muted-foreground">
              {worker.worker_code} • {worker.client?.display_name || worker.client_name}
            </p>
          </div>
        </div>
        <Button variant="outline" onClick={() => navigate(`/workers/${id}/edit`)}>
          Edit
        </Button>
      </div>

      {/* Basic Info Card */}
      <Card>
        <CardContent className="pt-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div>
              <p className="text-muted-foreground">Job Title</p>
              <p className="font-medium">{worker.job_title || '-'}</p>
            </div>
            <div>
              <p className="text-muted-foreground">Nationality</p>
              <p className="font-medium">{worker.nationality || '-'}</p>
            </div>
            <div>
              <p className="text-muted-foreground">Mobile</p>
              <p className="font-medium">{worker.mobile || '-'}</p>
            </div>
            <div>
              <p className="text-muted-foreground">Email</p>
              <p className="font-medium">{worker.email || '-'}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tabs for Document Cards and Full Documents */}
      <Tabs defaultValue="summary" className="w-full">
        <TabsList>
          <TabsTrigger value="summary" className="flex items-center gap-1">
            <FileText className="w-4 h-4" />
            Document Summary
          </TabsTrigger>
          <TabsTrigger value="documents" className="flex items-center gap-1">
            <FolderOpen className="w-4 h-4" />
            All Documents
          </TabsTrigger>
        </TabsList>

        <TabsContent value="summary" className="mt-4">
          {/* Document Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {docCards.map((doc) => (
              <Card key={doc.label}>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium flex items-center justify-between">
                    {doc.label}
                    {doc.status && (
                      <Badge className={getExpiryStatusClass(doc.days)}>{doc.status}</Badge>
                    )}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="font-mono text-sm">{doc.no || '-'}</p>
                  {doc.expiry && (
                    <p className="text-sm text-muted-foreground mt-1">
                      Expiry: {formatDate(doc.expiry)}
                      {doc.days !== undefined && <span className="ml-1">({doc.days}d)</span>}
                    </p>
                  )}
                  {doc.extra && <p className="text-xs text-muted-foreground mt-1">{doc.extra}</p>}
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="documents" className="mt-4">
          <DocumentsTab 
            entityType="Worker" 
            entityId={id} 
            entityName={worker.full_name}
          />
        </TabsContent>
      </Tabs>

      {/* Notes */}
      {worker.notes && (
        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Notes</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm whitespace-pre-wrap">{worker.notes}</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
